import { type ReactElement, useCallback, useEffect, useState } from "react";

import type { SearchResponse } from "src/api/SearchResponse";
import { isValidSegment } from "src/domain/SegmentScreen/useEnsureValidSegment";
import type { Geocoded } from "src/GeocodedPlace";
import { SearchOverrideProvider } from "src/utils/hooks/SearchOverrideProvider";
import { useIsRouteSkipped } from "src/utils/hooks/useIsRouteSkipped";
import {
  type ReturnStage,
  useTypedLocation,
} from "src/utils/hooks/useTypedLocation";
import styled from "styled-components";
import { useTripRouteIndex } from "../hooks/useTripRoute";
import { useTripRouteSegmentIndex } from "../hooks/useTripSegment";
import { MemoizedTransport } from "../Transport/Transport";
import { getScreenKey } from "../util/getScreenKey";

type Props = {
  AllPlaces: ReactElement;
  transportIndex?: number;
  returnsFlowLocation?: ReturnStage;
  searchResponse: SearchResponse | undefined;
  tripOrigin?: Geocoded;
  tripDestination?: Geocoded;
};

export function TransportResults({
  transportIndex,
  returnsFlowLocation,
  AllPlaces,
  searchResponse,
  tripOrigin,
  tripDestination,
}: Props) {
  const location = useTypedLocation();
  const screenKey = getScreenKey(location.hash);

  const routeIndex = useTripRouteIndex();
  const routeSegmentIndex = useTripRouteSegmentIndex();
  const isRouteSkipped = useIsRouteSkipped(routeIndex);
  const [validSegment, setValidSegment] = useState<boolean | undefined>();
  const isResultOpen = ["transport"].includes(screenKey ?? "");

  // If the active pane index is set to 0, the transport flow is animating out, and a rerender isn't necessary.
  // A rerender of the transport flow as it is animating out is expensive (and jittery), so this check prevents any animation jitter.

  const checkValidSegment = useCallback(() => {
    const isValid = isValidSegment(
      searchResponse,
      routeIndex,
      routeSegmentIndex,
      isRouteSkipped,
    );

    setValidSegment(isValid);
  }, [isRouteSkipped, routeIndex, routeSegmentIndex, searchResponse]);

  // In order to calculate whether the desktop two column experience should
  // be applied, we first need to check if the segment is valid.
  // We do not render the pane until this has been determined.

  useEffect(() => {
    if (searchResponse !== undefined && isResultOpen) {
      checkValidSegment();
    }
  }, [checkValidSegment, searchResponse, isResultOpen]);

  const PlacePairTransport = (
    <ScreenContainer>
      <SearchOverrideProvider
        value={{
          searchResponse: searchResponse!,
          origin: tripOrigin,
          destination: tripDestination,
        }}
      >
        {validSegment !== undefined && (
          <MemoizedTransport
            context="transport"
            transportIndex={transportIndex}
            returnsFlowLocation={returnsFlowLocation}
          />
        )}
      </SearchOverrideProvider>
    </ScreenContainer>
  );

  return isResultOpen ? PlacePairTransport : AllPlaces;
}

const ScreenContainer = styled.div`
  width: 100%;
  flex-grow: 1;

  display: flex;
  flex-direction: column;
`;
